// src/pages/Orders/ImplantOrderLabelModal.js
import React, { useState, useEffect } from "react";
import ImplantOrderLabel, {
  IMPLANT_LABEL_WIDTH_MM,
  IMPLANT_LABEL_HEIGHT_MM,
  BRAND_TEXT,
  DEFAULT_GTIN,
} from "./ImplantOrderLabel";
import { todayISO, addYears, buildImplantSpec } from "../Product/ImplantLabel";

/* ================= CHANGE THESE IF NEEDED ================= */
export const IFU_PDF_URL = "https://1drv.ms/b/c/e3c0305295e4ad2a/IQBKFEhTaL5OSaiYbs1EMDKtAQOze3A-m8sl434AiSVeOpY?e=ljp4UT";

export const FIXED_QTY = "1EA/PKG";
export const EXPIRY_YEARS = 5;
export const DEFAULT_SN = "HT0001";
export const DEFAULT_MOUNT = "Mount-Free (with Cover Screw)";

/* ============ SAVE / LOAD helpers (per product) ============ */
const STORAGE_KEY = (productId) => `implant_label_${productId || "default"}`;

const makeLot = (product, mfgDate) => {
  if (product?.lot_number) return String(product.lot_number);
  const compact = String(mfgDate || "").replace(/-/g, "").slice(2);
  return `HTP${product?.product_Id || ""}${compact}`;
};

const buildInitialFields = (product) => {
  const mfgDate = todayISO();
  const typeName = product?.type?.type_name || "";
  const brandName = product?.product_brand?.brand_name || "";

  return {
    brand: BRAND_TEXT,
    level: typeName,
    spec: buildImplantSpec(product),
    title: product?.product_Name || "",
    subtitle: typeName,
    compat: brandName ? `(compatible with ${brandName})` : "",
    ref: product?.reference || product?.SKU || "",
    lot: makeLot(product, mfgDate),
    sn: DEFAULT_SN,
    mfgDate,
    expDate: addYears(mfgDate, EXPIRY_YEARS),
    mount: DEFAULT_MOUNT,
    gtin: DEFAULT_GTIN,
    topQr: IFU_PDF_URL,
  };
};

const loadSavedFields = (product) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(product?.product_Id));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

const saveFieldsToStorage = (product, fields) => {
  try {
    localStorage.setItem(
      STORAGE_KEY(product?.product_Id),
      JSON.stringify(fields)
    );
    return true;
  } catch (e) {
    return false;
  }
};

const clearSavedFields = (product) => {
  try {
    localStorage.removeItem(STORAGE_KEY(product?.product_Id));
  } catch (e) {}
};

/* ================= STYLES ================= */
const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.6)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
  },
  box: {
    background: "#ffffff",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "980px",
    maxHeight: "94vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 20px",
    borderBottom: "1px solid #e2e8f0",
  },
  title: { margin: 0, fontSize: "18px", color: "#0f172a" },
  close: {
    border: "none",
    background: "transparent",
    fontSize: "26px",
    lineHeight: 1,
    cursor: "pointer",
    color: "#475569",
  },
  body: {
    display: "flex",
    gap: "20px",
    padding: "16px 20px",
    overflow: "auto",
    flex: 1,
    flexWrap: "wrap",
  },
  form: { flex: "1 1 300px", minWidth: "280px" },
  field: { marginBottom: "10px", display: "flex", flexDirection: "column" },
  label: { fontSize: "12px", fontWeight: 600, color: "#475569", marginBottom: "3px" },
  input: {
    padding: "7px 9px",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    fontSize: "13px",
  },
  readonly: { background: "#f1f5f9", cursor: "not-allowed" },
  previewWrap: {
    flex: "0 1 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    background: "#f1f5f9",
    borderRadius: "10px",
    padding: "14px",
    maxWidth: "100%",
    overflow: "auto",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    padding: "12px 20px",
    borderTop: "1px solid #e2e8f0",
    flexWrap: "wrap",
  },
  btnPrimary: {
    background: "#004aad",
    color: "#fff",
    border: "none",
    padding: "9px 18px",
    borderRadius: "8px",
    fontWeight: 600,
    cursor: "pointer",
  },
  btnSuccess: {
    background: "#16a34a",
    color: "#fff",
    border: "none",
    padding: "9px 18px",
    borderRadius: "8px",
    fontWeight: 600,
    cursor: "pointer",
  },
  btnDanger: {
    background: "#fff",
    color: "#dc2626",
    border: "1px solid #fecaca",
    padding: "9px 14px",
    borderRadius: "8px",
    fontWeight: 600,
    cursor: "pointer",
  },
  btnGhost: {
    background: "#fff",
    color: "#334155",
    border: "1px solid #cbd5e1",
    padding: "9px 18px",
    borderRadius: "8px",
    fontWeight: 600,
    cursor: "pointer",
  },
  toast: {
    position: "absolute",
    top: "70px",
    left: "50%",
    transform: "translateX(-50%)",
    background: "#16a34a",
    color: "#fff",
    padding: "10px 20px",
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "13px",
    boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
    zIndex: 10,
    pointerEvents: "none",
  },
};

const Field = ({ label, name, value, onChange, type = "text", readOnly = false }) => (
  <div style={styles.field}>
    <label style={styles.label}>{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      readOnly={readOnly}
      onChange={onChange}
      style={{ ...styles.input, ...(readOnly ? styles.readonly : null) }}
    />
  </div>
);

/* ================= THE MODAL ================= */
export const ImplantOrderLabelModal = ({ product, onClose }) => {
  const [fields, setFields] = useState(() => {
    // Load saved values if any, otherwise build defaults
    const saved = loadSavedFields(product);
    return saved ? { ...buildInitialFields(product), ...saved } : buildInitialFields(product);
  });
  const [landscape, setLandscape] = useState(false);
  const [toast, setToast] = useState("");
  const [hasSaved, setHasSaved] = useState(() => !!loadSavedFields(product));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "mfgDate") next.expDate = addYears(value, EXPIRY_YEARS);
      return next;
    });
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2200);
  };

  /* ⭐ SAVE button — writes current fields to localStorage (per product) */
  const handleSave = () => {
    const ok = saveFieldsToStorage(product, fields);
    if (ok) {
      setHasSaved(true);
      showToast("Saved! Next time this will load automatically.");
    } else {
      showToast("Could not save. Storage might be full.");
    }
  };

  /* ⭐ RESET button — clears saved values and restores defaults */
  const handleReset = () => {
    clearSavedFields(product);
    setFields(buildInitialFields(product));
    setHasSaved(false);
    showToast("Reset to defaults.");
  };

  const labelData = { ...fields, qty: FIXED_QTY };

  const handlePrint = () => {
    const svg = document.getElementById("implant-order-label");
    if (!svg) return;

    const pageW = landscape ? IMPLANT_LABEL_HEIGHT_MM : IMPLANT_LABEL_WIDTH_MM;
    const pageH = landscape ? IMPLANT_LABEL_WIDTH_MM : IMPLANT_LABEL_HEIGHT_MM;

    const win = window.open("", "_blank");
    if (!win) {
      alert("Please allow pop-ups to print the label.");
      return;
    }

    win.document.write(`<!DOCTYPE html>
<html>
  <head>
    <title>Implant Order Label</title>
    <style>
      @page { size: ${pageW}mm ${pageH}mm; margin: 0; }
      html, body { margin: 0; padding: 0; background: #fff; }
      svg { display: block; }
      .preview-only { display: none; }
    </style>
  </head>
  <body>${svg.outerHTML}</body>
</html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={{ ...styles.box, position: "relative" }} onClick={(e) => e.stopPropagation()}>
        {toast && <div style={styles.toast}>{toast}</div>}

        <div style={styles.header}>
          <h2 style={styles.title}>
            Implant Label (50mm × 165mm) — {product?.product_Name}
          </h2>
          <button style={styles.close} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div style={styles.body}>
          <div style={styles.form}>
            <Field label="Product title (centre)" name="title" value={fields.title} onChange={handleChange} />
            <Field label="Sub title (e.g. Bone level)" name="subtitle" value={fields.subtitle} onChange={handleChange} />
            <Field label="Compatible with" name="compat" value={fields.compat} onChange={handleChange} />
            <Field label="Top panel level" name="level" value={fields.level} onChange={handleChange} />
            <Field label="Top panel size / variation" name="spec" value={fields.spec} onChange={handleChange} />
            <Field label="REF" name="ref" value={fields.ref} onChange={handleChange} />
            <Field label="LOT" name="lot" value={fields.lot} onChange={handleChange} />
            <Field label="QTY (fixed)" name="qty" value={FIXED_QTY} onChange={() => {}} readOnly />
            <Field label="SN" name="sn" value={fields.sn} onChange={handleChange} />
            <Field label="Manufacture date" name="mfgDate" type="date" value={fields.mfgDate} onChange={handleChange} />
            <Field label="Expiry date" name="expDate" type="date" value={fields.expDate} onChange={handleChange} />
            <Field label="Mount text" name="mount" value={fields.mount} onChange={handleChange} />
            <Field label="GTIN" name="gtin" value={fields.gtin} onChange={handleChange} />
            <Field label="QR link (PDF opens on scan)" name="topQr" value={fields.topQr} onChange={handleChange} />
          </div>

          <div style={styles.previewWrap}>
            <ImplantOrderLabel data={labelData} preview landscape={landscape} />
          </div>
        </div>

        <div style={styles.footer}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
            <label style={{ fontSize: "13px", color: "#334155", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={landscape}
                onChange={(e) => setLandscape(e.target.checked)}
                style={{ marginRight: "6px" }}
              />
              Landscape (165mm × 50mm)
            </label>

            {hasSaved && (
              <span style={{ fontSize: "12px", color: "#16a34a", fontWeight: 600 }}>
                ● Saved data loaded
              </span>
            )}
          </div>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button style={styles.btnDanger} onClick={handleReset} title="Clear saved values and restore defaults">
              Reset
            </button>
            <button style={styles.btnSuccess} onClick={handleSave} title="Save these values for next time">
              💾 Save
            </button>
            <button style={styles.btnGhost} onClick={onClose}>
              Close
            </button>
            <button style={styles.btnPrimary} onClick={handlePrint}>
              Print / Save as PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImplantOrderLabelModal;